// features/hives/ui/hivesHead/HivesHead.tsx
import {useMemo, useRef} from 'react';
import {Input} from '@atomaro/ui-kit';
import Button from '@atomaro/ui-kit/components/Button/Button/Button';
import Popover from '@atomaro/ui-kit/components/Popover/Popover';
import TagItem from '@atomaro/ui-kit/components/Tag/TagItem/TagItem';
import {IDropdownMenuItem} from '@atomaro/ui-kit/components/DropdownMenu/types';
import {HivesSearch} from 'shared/ui/icons/hivesSearch';
import {FiltersIcon} from 'shared/ui/icons/filtersIcon';
import {AddLarge} from 'shared/ui/icons';
import {SortSelect} from 'shared/ui/sortSelect';
import {useResize} from 'app/lib/hooks/useResize';
import {COLORS} from 'shared/consts';
import {useHivesFilters} from 'features/hives/lib/useHivesFilters';
import {HivesFiltersRangeItem} from '../hivesFilters/HivesFiltersRangeItem';
import {HivesHeadRow, HivesHeadWrapper, InputContainer} from './styles';
import {useHivesStore} from 'entities/hives';
import {HivesDictionariesFilter} from 'features/hives/ui/hivesFilters/HivesDictionariesFilter';
import {HivesFilterDrawer} from 'features/hives/ui/hivesFilters/HivesFilterDrawer';
import {Drawer} from 'shared/ui/drawer';
import {MiniCloseIcon} from 'shared/ui/icons/minicloseicon';
import {ArrowDownIcon} from 'shared/ui/icons/arrowDown';

export const HivesHead = () => {
  const {isScreenMd} = useResize();

  const sortItems = useRef<IDropdownMenuItem[]>([
    {key: 'number', value: 'Номер улья'},
    {key: 'uterus.year', value: 'Год матки'},
    {key: 'strength', value: 'Сила'},
    {key: 'brood', value: 'Расплод'},
    {key: 'updatedAt', value: 'Обновлено'},
  ]);

  const {
    control,
    formValues,
    dictionaryItems,
    settedFiltersCounter,
    shortFilterOpenSelector,
    shortLabels,
    isFiltersOpen,
    setIsFiltersOpen,
    openShortFilter,
    closeShortFilter,
    onSearchChange,
    onRangeChange,
    onSortChange,
    setHasDictionary,
    onDictionaryReset,
    onResetField,
    onResetAll,
    submit,
  } = useHivesFilters();
  const setIsNewOpen = useHivesStore((s) => s.managePopup);

  const showResetAll = useMemo(() => {
    return (
      Boolean(formValues?.framesFrom?.trim() || formValues?.framesTo?.trim()) ||
      Boolean(formValues?.powerFrom?.trim() || formValues?.powerTo?.trim()) ||
      Boolean(formValues?.broodFrom?.trim() || formValues?.broodTo?.trim()) ||
      Boolean((formValues?.dictionariesIds || []).length)
    );
  }, [formValues]);

  const closeTagFilter = (name: string) => {
    onResetField(name);
    submit(true);
  };

  return (
    <HivesHeadWrapper>
      <HivesHeadRow>
        <InputContainer>
          <Input
            size="m"
            placeholder="Поиск по номеру улья"
            iconPrefix={<HivesSearch />}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </InputContainer>

        {isScreenMd && (
          <>
            <SortSelect
              items={sortItems.current}
              defaultValue="updatedAt"
              value={formValues?.sortBy}
              direction={(formValues?.sort as 'ASC' | 'DESC') || 'DESC'}
              onChange={onSortChange}
            />
            <Button
              label={`Фильтры${settedFiltersCounter ? `: ${settedFiltersCounter}` : ''}`}
              iconPrefix={
                <FiltersIcon fill={settedFiltersCounter ? COLORS.ACCENT.DEFAULT : undefined} />
              }
              variant="secondary"
              colorScheme={settedFiltersCounter ? 'accent' : 'neutral'}
              onClick={() => setIsFiltersOpen(true)}
            />
            <Button
              label="Новый улей"
              iconPrefix={<AddLarge />}
              style={{marginLeft: 'auto'}}
              onClick={() => setIsNewOpen(true)}
            />
          </>
        )}
      </HivesHeadRow>

      {isScreenMd ? (
        <HivesHeadRow>
          <Popover
            isOpened={shortFilterOpenSelector === 'frames'}
            placement="bottomLeft"
            pointer={false}
            useInPortal={false}
            className="popover_nopadding"
            onClose={() => shortFilterOpenSelector === 'frames' && closeShortFilter()}
            innerChildren={
              <HivesFiltersRangeItem
                name="Рамки"
                control={control}
                fieldNames={{from: 'framesFrom', to: 'framesTo'}}
                onChange={onRangeChange}
                showFooter
                showReset={!!shortLabels.frames}
                onSubmitClick={() => {
                  submit();
                  closeShortFilter(true);
                }}
                onResetClick={() => onResetField('frames')}
              />
            }
          >
            <TagItem
              onClick={() =>
                shortFilterOpenSelector === 'frames'
                  ? closeShortFilter()
                  : openShortFilter('frames')
              }
              onClose={() => closeTagFilter('frames')}
              closable={!!shortLabels.frames}
            >
              Рамки{shortLabels.frames ? `: ${shortLabels.frames}` : ''}
              {!shortLabels.frames ? (
                shortFilterOpenSelector === 'frames' ? (
                  <MiniCloseIcon style={{margin: '0 0 0 5px', pointerEvents: 'none'}} />
                ) : (
                  <ArrowDownIcon style={{margin: '0 0 0 5px', pointerEvents: 'none'}} />
                )
              ) : (
                ''
              )}
            </TagItem>
          </Popover>

          <Popover
            isOpened={shortFilterOpenSelector === 'power'}
            placement="bottomLeft"
            pointer={false}
            useInPortal={false}
            className="popover_nopadding"
            onClose={() => shortFilterOpenSelector === 'power' && closeShortFilter()}
            innerChildren={
              <HivesFiltersRangeItem
                name="Сила"
                control={control}
                fieldNames={{from: 'powerFrom', to: 'powerTo'}}
                onChange={onRangeChange}
                showFooter
                showReset={!!shortLabels.power}
                onSubmitClick={() => {
                  submit();
                  closeShortFilter(true);
                }}
                onResetClick={() => onResetField('power')}
              />
            }
          >
            <TagItem
              onClick={() =>
                shortFilterOpenSelector === 'power' ? closeShortFilter() : openShortFilter('power')
              }
              onClose={() => closeTagFilter('power')}
              closable={!!shortLabels.power}
            >
              Сила{shortLabels.power ? `: ${shortLabels.power}` : ''}
              {!shortLabels.power ? (
                shortFilterOpenSelector === 'power' ? (
                  <MiniCloseIcon style={{margin: '0 0 0 5px', pointerEvents: 'none'}} />
                ) : (
                  <ArrowDownIcon style={{margin: '0 0 0 5px', pointerEvents: 'none'}} />
                )
              ) : (
                ''
              )}
            </TagItem>
          </Popover>

          <Popover
            isOpened={shortFilterOpenSelector === 'brood'}
            placement="bottomLeft"
            pointer={false}
            useInPortal={false}
            className="popover_nopadding"
            onClose={() => shortFilterOpenSelector === 'brood' && closeShortFilter()}
            innerChildren={
              <HivesFiltersRangeItem
                name="Расплод"
                control={control}
                fieldNames={{from: 'broodFrom', to: 'broodTo'}}
                onChange={onRangeChange}
                showFooter
                showReset={!!shortLabels.brood}
                onSubmitClick={() => {
                  submit();
                  closeShortFilter(true);
                }}
                onResetClick={() => onResetField('brood')}
              />
            }
          >
            <TagItem
              onClick={() =>
                shortFilterOpenSelector === 'brood' ? closeShortFilter() : openShortFilter('brood')
              }
              onClose={() => closeTagFilter('brood')}
              closable={!!shortLabels.brood}
            >
              Расплод{shortLabels.brood ? `: ${shortLabels.brood}` : ''}
              {!shortLabels.brood ? (
                shortFilterOpenSelector === 'brood' ? (
                  <MiniCloseIcon style={{margin: '0 0 0 5px', pointerEvents: 'none'}} />
                ) : (
                  <ArrowDownIcon style={{margin: '0 0 0 5px', pointerEvents: 'none'}} />
                )
              ) : (
                ''
              )}
            </TagItem>
          </Popover>

          <Popover
            isOpened={shortFilterOpenSelector === 'dictionariesIds'}
            placement="bottomLeft"
            pointer={false}
            useInPortal={false}
            className="popover_nopadding"
            onClose={() => shortFilterOpenSelector === 'dictionariesIds' && closeShortFilter()}
            innerChildren={
              <HivesDictionariesFilter
                items={dictionaryItems}
                onItemClick={setHasDictionary}
                onReset={onDictionaryReset}
              />
            }
          >
            <TagItem
              onClick={() =>
                shortFilterOpenSelector === 'dictionariesIds'
                  ? closeShortFilter()
                  : openShortFilter('dictionariesIds')
              }
              onClose={() => closeTagFilter('dictionariesIds')}
              closable={!!shortLabels.dictionariesIds}
            >
              Состояние{shortLabels.dictionariesIds ? `: ${shortLabels.dictionariesIds}` : ''}
              {!shortLabels.dictionariesIds ? (
                shortFilterOpenSelector === 'dictionariesIds' ? (
                  <MiniCloseIcon style={{margin: '0 0 0 5px', pointerEvents: 'none'}} />
                ) : (
                  <ArrowDownIcon style={{margin: '0 0 0 5px', pointerEvents: 'none'}} />
                )
              ) : (
                ''
              )}
            </TagItem>
          </Popover>
        </HivesHeadRow>
      ) : (
        <HivesHeadRow $overflow="auto hidden">
          <SortSelect
            items={sortItems.current}
            isMobile
            value={formValues?.sortBy}
            direction={(formValues?.sort as 'ASC' | 'DESC') || 'DESC'}
            onChange={onSortChange}
          />
          <TagItem onClick={() => setIsFiltersOpen(true)}>
            <FiltersIcon />
          </TagItem>

          <TagItem
            onClick={() => openShortFilter('frames')}
            onClose={() => onResetField('frames')}
            closable={!!shortLabels.frames}
          >
            Рамки{shortLabels.frames ? `: ${shortLabels.frames}` : ''}
          </TagItem>
          <TagItem
            onClick={() => openShortFilter('power')}
            onClose={() => onResetField('power')}
            closable={!!shortLabels.power}
          >
            Сила{shortLabels.power ? `: ${shortLabels.power}` : ''}
          </TagItem>
          <TagItem
            onClick={() => openShortFilter('brood')}
            onClose={() => onResetField('brood')}
            closable={!!shortLabels.brood}
          >
            Расплод{shortLabels.brood ? `: ${shortLabels.brood}` : ''}
          </TagItem>
          <TagItem
            onClick={() => openShortFilter('dictionariesIds')}
            onClose={() => onResetField('dictionariesIds')}
            closable={!!shortLabels.dictionariesIds}
          >
            Состояние{shortLabels.dictionariesIds ? `: ${shortLabels.dictionariesIds}` : ''}
          </TagItem>

          <TagItem onClick={() => setIsNewOpen(true)}>
            <AddLarge />
          </TagItem>

          <Drawer
            isOpen={shortFilterOpenSelector === 'frames'}
            position="bottom"
            onClose={() => shortFilterOpenSelector === 'frames' && closeShortFilter()}
          >
            <HivesFiltersRangeItem
              name="Рамки"
              control={control}
              fieldNames={{from: 'framesFrom', to: 'framesTo'}}
              onChange={onRangeChange}
              showFooter
              showReset={!!shortLabels.frames}
              onSubmitClick={() => {
                submit();
                closeShortFilter(true);
              }}
              onResetClick={() => onResetField('frames')}
            />
          </Drawer>

          <Drawer
            isOpen={shortFilterOpenSelector === 'power'}
            position="bottom"
            onClose={() => shortFilterOpenSelector === 'power' && closeShortFilter()}
          >
            <HivesFiltersRangeItem
              name="Сила"
              control={control}
              fieldNames={{from: 'powerFrom', to: 'powerTo'}}
              onChange={onRangeChange}
              showFooter
              showReset={!!shortLabels.power}
              onSubmitClick={() => {
                submit();
                closeShortFilter(true);
              }}
              onResetClick={() => onResetField('power')}
            />
          </Drawer>

          <Drawer
            isOpen={shortFilterOpenSelector === 'brood'}
            position="bottom"
            onClose={() => shortFilterOpenSelector === 'brood' && closeShortFilter()}
          >
            <HivesFiltersRangeItem
              name="Расплод"
              control={control}
              fieldNames={{from: 'broodFrom', to: 'broodTo'}}
              onChange={onRangeChange}
              showFooter
              showReset={!!shortLabels.brood}
              onSubmitClick={() => {
                submit();
                closeShortFilter(true);
              }}
              onResetClick={() => onResetField('brood')}
            />
          </Drawer>

          <Drawer
            isOpen={shortFilterOpenSelector === 'dictionariesIds'}
            position="bottom"
            onClose={() => {
              if (shortFilterOpenSelector === 'dictionariesIds') {
                submit();
                closeShortFilter();
              }
            }}
          >
            <HivesDictionariesFilter
              items={dictionaryItems}
              onItemClick={setHasDictionary}
              onReset={onDictionaryReset}
            />
          </Drawer>
        </HivesHeadRow>
      )}

      <HivesFilterDrawer
        isOpen={isFiltersOpen}
        control={control}
        shortLabels={shortLabels}
        onClose={() => setIsFiltersOpen(false)}
        onRangeChange={onRangeChange}
        onResetField={onResetField}
        dictionaryItems={dictionaryItems}
        onDictionaryItemClick={setHasDictionary}
        onDictionaryReset={onDictionaryReset}
        onSubmit={submit}
        showResetAll={showResetAll}
        onAllReset={onResetAll}
      />
    </HivesHeadWrapper>
  );
};

export default HivesHead;
